/**
 * ファーストビュー：背景ケンバーンズ＋テキスト＋太陽＋レンズフレア
 */
let visual = () => {
  const wrap = document.querySelector('.p-visual');
  if (!wrap) return;

  const items = gsap.utils.toArray('.js-fv-item');
  const sun = wrap.querySelector('.p-visual__sun');
  const glow = wrap.querySelector('.p-visual__sun-glow');
  const core = wrap.querySelector('.p-visual__sun-core');
  const canvas = wrap.querySelector('.p-visual__flare-canvas');

  // 初期状態
  gsap.set(items, { opacity: 0, y: 24 });
  gsap.set(sun, { opacity: 0 });

  // メインタイムライン
  const tl = gsap.timeline({ delay: 0.3 });

  tl.to(items, { opacity: 1, y: 0, duration: 0.8, stagger: 0.18, ease: 'power2.out' });
  tl.to(sun, { opacity: 1, duration: 2.0, ease: 'power1.out' }, '-=0.6');

  // CSSグロウのパルス
  if (glow) {
    gsap.to(glow, {
      scale: 1.07,
      opacity: 0.8,
      duration: 4,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      transformOrigin: '62% 28%',
    });
  }

  // 太陽コアの煌めき
  if (core) {
    gsap.to(core, {
      scale: 1.15,
      duration: 2.5,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      transformOrigin: 'center center',
    });
  }

  // Canvas レンズフレア
  if (canvas) {
    initFlare(canvas, core);
  }

  // ─────────────────────────────────────────────
  // スクロールパララックス
  // height: 120% にした kv-zoom を初期状態で中央寄せし、
  // スクロールに合わせてゆっくり上へずらす
  // ─────────────────────────────────────────────
  const kvZooms = wrap.querySelectorAll('.kv-zoom');
  if (kvZooms.length) {
    // 中央寄せ：(120%-100%)/2 = 10dvh 上にオフセット
    // yPercent = -10dvh / 120dvh * 100 = -8.33%
    gsap.set(kvZooms, { yPercent: 0 });

    gsap.to(kvZooms, {
      yPercent: 30,
      ease: 'none',
      scrollTrigger: {
        trigger: wrap,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }
};

// ─────────────────────────────────────────────
// CSS太陽コアのDOM位置をCanvas座標に変換
// ─────────────────────────────────────────────

function computeSunPos(canvas, core) {
  const canvasRect = canvas.getBoundingClientRect();
  const coreRect = core.getBoundingClientRect();
  const sx = coreRect.left + coreRect.width * 0.5 - canvasRect.left;
  const sy = coreRect.top + coreRect.height * 0.5 - canvasRect.top;
  return { sx, sy };
}

// ─────────────────────────────────────────────
// レンズフレア（Canvas API）
// ─────────────────────────────────────────────

function initFlare(canvas, core) {
  const ctx = canvas.getContext('2d');
  let w, h;

  // ベース intensity（GSAP tweenが更新する）
  const state = { base: 0 };

  const getSunPos = () => {
    if (core) {
      return computeSunPos(canvas, core);
    }
    return { sx: w * 0.84, sy: h * 0.1 };
  };

  const resize = () => {
    const dpr = window.devicePixelRatio || 1;
    w = canvas.offsetWidth;
    h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  // GASPティッカー：毎フレーム多周波シマー＋描画
  let tickerActive = false;
  const tick = () => {
    if (state.base <= 0) return;
    const t = gsap.ticker.time;
    // 複数周波数の正弦波を重ねてキラキラ感を演出
    const shimmer =
      1 +
      Math.sin(t * 0.8) * 0.18 +
      Math.sin(t * 3.1) * 0.1 +
      Math.sin(t * 9.3) * 0.05 +
      Math.sin(t * 17.7) * 0.02;
    const intensity = state.base * shimmer;
    ctx.clearRect(0, 0, w, h);
    drawFlare(ctx, w, h, intensity, getSunPos());
  };

  // フェードイン → パルスループ
  gsap.to(state, {
    base: 1,
    duration: 2.5,
    delay: 1.0,
    ease: 'power2.out',
    onStart: () => {
      if (!tickerActive) {
        gsap.ticker.add(tick);
        tickerActive = true;
      }
    },
    onComplete: () => {
      gsap.to(state, {
        base: 0.5,
        duration: 3.5,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    },
  });

  resize();
  window.addEventListener('resize', resize);
}

/**
 * フレア描画
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w  - CSS幅
 * @param {number} h  - CSS高さ
 * @param {number} intensity - 0〜1
 * @param {{ sx: number, sy: number }} sunPos
 */
function drawFlare(ctx, w, h, intensity, { sx, sy }) {
  // 画面中心（ゴーストの基点）
  const cx = w * 0.5;
  const cy = h * 0.5;

  // 太陽→中心のベクトル（ゴーストはこの方向に並ぶ）
  const dx = cx - sx;
  const dy = cy - sy;

  ctx.save();

  // ① サンブルーム
  const bloom = ctx.createRadialGradient(sx, sy, 0, sx, sy, w * 0.28);
  bloom.addColorStop(0, `rgba(255, 255, 240, ${0.9 * intensity})`);
  bloom.addColorStop(0.06, `rgba(255, 252, 210, ${0.55 * intensity})`);
  bloom.addColorStop(0.22, `rgba(255, 245, 180, ${0.18 * intensity})`);
  bloom.addColorStop(0.5, `rgba(255, 235, 140, ${0.04 * intensity})`);
  bloom.addColorStop(1, 'rgba(255, 220, 100, 0)');
  ctx.fillStyle = bloom;
  ctx.beginPath();
  ctx.arc(sx, sy, w * 0.3, 0, Math.PI * 2);
  ctx.fill();

  // ② 斜めストリーク（水平ストリークと同系の青白色・半分サイズ）
  ctx.save();
  ctx.translate(sx, sy);
  ctx.rotate((-35 * Math.PI) / 180);
  const diagLen = Math.max(w, h) * 0.53;

  const drawDiagLayer = (halfW, alpha) => {
    const grad = ctx.createLinearGradient(-diagLen, 0, diagLen, 0);
    grad.addColorStop(0, 'rgba(160, 210, 255, 0)');
    grad.addColorStop(0.3, `rgba(180, 225, 255, ${alpha * intensity})`);
    grad.addColorStop(0.48, `rgba(220, 242, 255, ${alpha * intensity * 1.3})`);
    grad.addColorStop(0.5, `rgba(255, 255, 255, ${alpha * intensity * 1.5})`);
    grad.addColorStop(0.52, `rgba(220, 242, 255, ${alpha * intensity * 1.3})`);
    grad.addColorStop(0.7, `rgba(180, 225, 255, ${alpha * intensity})`);
    grad.addColorStop(1, 'rgba(160, 210, 255, 0)');
    ctx.strokeStyle = grad;
    ctx.lineWidth = halfW * 2;
    ctx.beginPath();
    ctx.moveTo(-diagLen, 0);
    ctx.lineTo(diagLen, 0);
    ctx.stroke();
  };

  drawDiagLayer(35, 0.06);
  drawDiagLayer(15, 0.12);
  drawDiagLayer(6, 0.22);
  drawDiagLayer(2, 0.38);
  drawDiagLayer(0.8, 0.65);
  ctx.restore();

  // ③ アナモルフィックストリーク（水平・青白い補助線）
  ctx.save();
  const sRatio = sx / w;
  const streakGrad = ctx.createLinearGradient(0, 0, w, 0);
  streakGrad.addColorStop(0, 'rgba(160, 210, 255, 0)');
  streakGrad.addColorStop(Math.max(0, sRatio - 0.38), 'rgba(160, 210, 255, 0)');
  streakGrad.addColorStop(sRatio, `rgba(210, 238, 255, ${0.4 * intensity})`);
  streakGrad.addColorStop(Math.min(1, sRatio + 0.38), 'rgba(160, 210, 255, 0)');
  streakGrad.addColorStop(1, 'rgba(160, 210, 255, 0)');

  ctx.beginPath();
  ctx.moveTo(0, sy);
  ctx.lineTo(w, sy);
  ctx.strokeStyle = streakGrad;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(0, sy);
  ctx.lineTo(w, sy);
  ctx.strokeStyle = streakGrad;
  ctx.lineWidth = 10;
  ctx.globalAlpha = 0.22 * intensity;
  ctx.stroke();
  ctx.restore();

  // ④ ゴースト群
  const ghosts = [
    { t: 0.32, r: 26, rgb: [185, 130, 235], a: 0.38 },
    { t: 0.58, r: 44, rgb: [80, 150, 245], a: 0.28 },
    { t: 0.82, r: 16, rgb: [245, 175, 65], a: 0.36 },
    { t: 1.1, r: 74, rgb: [65, 110, 225], a: 0.15 },
    { t: 1.36, r: 32, rgb: [215, 88, 170], a: 0.24 },
    { t: 1.65, r: 18, rgb: [95, 200, 140], a: 0.3 },
  ];

  ghosts.forEach(({ t, r, rgb, a }) => {
    const gx = sx + dx * t;
    const gy = sy + dy * t;
    const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, r);
    const [rv, gv, bv] = rgb;
    grad.addColorStop(0, `rgba(${rv}, ${gv}, ${bv}, ${a * intensity})`);
    grad.addColorStop(0.45, `rgba(${rv}, ${gv}, ${bv}, ${a * 0.45 * intensity})`);
    grad.addColorStop(1, `rgba(${rv}, ${gv}, ${bv}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(gx, gy, r, 0, Math.PI * 2);
    ctx.fill();
  });

  // ⑤ 回折リング（ハロ）
  ctx.beginPath();
  ctx.arc(sx, sy, w * 0.09, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(255, 228, 140, ${0.22 * intensity})`;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();
}

/**
 * 初期化
 */
let init = () => {
  visual();
};

export default { init, visual };
